import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Plus, Pencil, FileText, CalendarRange } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { api, euro, dataOraIt, formatError, STATI_NOLEGGIO } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { ConfirmDelete } from "@/components/ConfirmDelete";
import { WhatsAppButton } from "@/components/WhatsAppButton";
import { NoleggioDialog } from "@/components/NoleggioDialog";

const HEAD = ["N°", "Cliente", "Bici", "Inizio", "Fine prevista", "Totale", "Stato", ""];
const STILE = { prenotato: "border-amber-300", in_corso: "border-sky-300", restituito: "border-emerald-300", annullato: "border-slate-200" };

const messaggio = (n, negozio) =>
  `Ciao ${n.cliente_nome.split(" ")[0]}, ti confermiamo il noleggio ${n.numero} (${n.bici_desc}) dal ${dataOraIt(n.inizio)} al ${dataOraIt(n.fine)}. Totale ${euro(n.totale)}${n.cauzione ? `, cauzione ${euro(n.cauzione)}` : ""}. Ricorda di portare un documento d'identità. ${negozio || ""}`.trim();

export default function Noleggi() {
  const [stato, setStato] = useState("attivi");
  const [items, setItems] = useState([]);
  const [clienti, setClienti] = useState([]);
  const [flotta, setFlotta] = useState([]);
  const [negozio, setNegozio] = useState("");
  const [dlg, setDlg] = useState(null);
  const [sp, setSp] = useSearchParams();
  const navigate = useNavigate();

  const load = useCallback(() => api.get("/noleggi").then((r) => setItems(r.data)).catch((e) => toast.error(formatError(e))), []);
  useEffect(() => {
    load();
    Promise.all([api.get("/clienti"), api.get("/bici_noleggio"), api.get("/impostazioni")]).then(([c, f, i]) => {
      setClienti(c.data); setFlotta(f.data); setNegozio(i.data.nome_negozio || "");
      if (sp.get("apri")) {
        api.get(`/noleggi/${sp.get("apri")}`).then((r) => setDlg(r.data)).catch(() => {});
        setSp({}, { replace: true });
      } else if (sp.get("cliente") || sp.get("bici")) {
        const ini = sp.get("inizio");
        const bid = sp.get("bici");
        const fine = ini ? new Date(new Date(ini).getTime() + 864e5) : null;
        const p = (n) => String(n).padStart(2, "0");
        setDlg({
          cliente_id: sp.get("cliente") || "",
          ...(bid ? { bici_ids: [bid], cauzione: f.data.find((b) => b.id === bid)?.cauzione || 0 } : {}),
          ...(ini ? { inizio: ini, fine: `${fine.getFullYear()}-${p(fine.getMonth() + 1)}-${p(fine.getDate())}T${p(fine.getHours())}:${p(fine.getMinutes())}` } : {}),
        });
        setSp({}, { replace: true });
      }
    });
  }, [load]); // eslint-disable-line react-hooks/exhaustive-deps

  const visibili = items.filter((n) => stato === "tutti" || (stato === "attivi" ? ["prenotato", "in_corso"].includes(n.stato) : n.stato === stato));
  const cambia = async (n, s) => {
    try { await api.patch(`/noleggi/${n.id}/stato`, { stato: s }); toast.success("Stato aggiornato"); load(); } catch (e) { toast.error(formatError(e)); }
  };
  const elimina = async (id) => {
    try { await api.delete(`/noleggi/${id}`); toast.success("Noleggio eliminato"); load(); } catch (e) { toast.error(formatError(e)); }
  };

  return (
    <div>
      <PageHeader overline="Noleggio" title="Noleggi">
        <Button variant="outline" onClick={() => navigate("/flotta/calendario")} data-testid="noleggi-calendario-button"><CalendarRange className="mr-1 h-4 w-4" /> Calendario flotta</Button>
        <Button onClick={() => setDlg({})} className="bg-orange-600 hover:bg-orange-700" data-testid="noleggi-new-button"><Plus className="mr-1 h-4 w-4" /> Nuovo noleggio</Button>
      </PageHeader>
      <Tabs value={stato} onValueChange={setStato} className="mb-4">
        <TabsList className="flex h-auto flex-wrap justify-start bg-white ring-1 ring-slate-200">
          <TabsTrigger value="attivi" data-testid="noleggi-filter-attivi-tab">Attivi</TabsTrigger>
          {STATI_NOLEGGIO.map((s) => <TabsTrigger key={s.value} value={s.value} data-testid={`noleggi-filter-${s.value}-tab`}>{s.label}</TabsTrigger>)}
          <TabsTrigger value="tutti" data-testid="noleggi-filter-tutti-tab">Tutti</TabsTrigger>
        </TabsList>
      </Tabs>
      <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white shadow-sm">
        <Table data-testid="noleggi-table">
          <TableHeader><TableRow className="bg-slate-50 hover:bg-slate-50">{HEAD.map((h) => <TableHead key={h} className="text-[11px] font-bold uppercase tracking-wider text-slate-500">{h}</TableHead>)}</TableRow></TableHeader>
          <TableBody>
            {visibili.map((n) => (
              <TableRow key={n.id} data-testid={`noleggi-row-${n.id}`}>
                <TableCell className="font-mono text-xs font-bold text-orange-600">{n.numero}</TableCell>
                <TableCell><p className="font-semibold">{n.cliente_nome}</p><p className="text-xs text-slate-500">{n.documento}</p></TableCell>
                <TableCell className="max-w-[220px] text-sm">{n.bici_desc}</TableCell>
                <TableCell className="whitespace-nowrap font-mono text-xs">{dataOraIt(n.inizio)}</TableCell>
                <TableCell className="whitespace-nowrap font-mono text-xs">{dataOraIt(n.fine)}</TableCell>
                <TableCell className="font-mono font-bold">{euro(n.totale)}{n.cauzione ? <p className="text-[11px] font-normal text-slate-400">cauz. {euro(n.cauzione)}</p> : null}</TableCell>
                <TableCell>
                  <Select value={n.stato} onValueChange={(v) => cambia(n, v)}>
                    <SelectTrigger className={`h-8 w-36 text-xs ${STILE[n.stato]}`} data-testid={`noleggi-stato-select-${n.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{STATI_NOLEGGIO.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                  </Select>
                </TableCell>
                <TableCell className="whitespace-nowrap text-right">
                  <WhatsAppButton compact telefono={n.cliente_telefono} text={messaggio(n, negozio)} testId={`noleggi-whatsapp-${n.id}`} />
                  <Button variant="ghost" size="icon" title="Contratto" onClick={() => navigate(`/noleggi/${n.id}/contratto`)} data-testid={`noleggi-contratto-${n.id}`}><FileText className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" onClick={() => setDlg(n)} data-testid={`noleggi-edit-${n.id}`}><Pencil className="h-4 w-4" /></Button>
                  <ConfirmDelete testId={`noleggi-delete-${n.id}`} onConfirm={() => elimina(n.id)} label="Elimina noleggio" description="Il noleggio verrà eliminato definitivamente." />
                </TableCell>
              </TableRow>
            ))}
            {!visibili.length && <TableRow><TableCell colSpan={8} className="py-16 text-center text-sm text-slate-500" data-testid="noleggi-empty">Nessun noleggio.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
      <NoleggioDialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)} item={dlg} clienti={clienti} flotta={flotta} onSaved={load} />
    </div>
  );
}
